import { supabase } from "@/integrations/supabase/client";
import { getInferredCity, recordCitySignal } from "@/lib/inferred-city";

// Capture user's approximate location via free IP geolocation API
export const captureLocation = async (): Promise<{
  country: string | null;
  city: string | null;
  region: string | null;
}> => {
  try {
    const response = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(5000) });
    if (!response.ok) return { country: null, city: null, region: null };
    const data = await response.json();
    return {
      country: data.country_name || data.country || null,
      city: data.city || null,
      region: data.region || null,
    };
  } catch {
    return { country: null, city: null, region: null };
  }
};

// Alias for signup-specific usage
export const captureSignupLocation = captureLocation;

// Approximate coordinates (IP-based, no permission prompt) used to bias
// location search results toward the user's area. Cached per session.
const COORDS_CACHE_KEY = "approx_coords";

export const getApproxCoords = async (): Promise<{ lat: number; lng: number } | null> => {
  try {
    const cached = sessionStorage.getItem(COORDS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (typeof parsed?.lat === "number" && typeof parsed?.lng === "number") return parsed;
      return null;
    }
  } catch {
    // ignore cache read issues
  }

  try {
    const response = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(5000) });
    if (!response.ok) return null;
    const data = await response.json();
    const lat = Number(data.latitude);
    const lng = Number(data.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const coords = { lat, lng };
    try {
      sessionStorage.setItem(COORDS_CACHE_KEY, JSON.stringify(coords));
    } catch {
      // ignore cache write issues
    }
    return coords;
  } catch {
    return null;
  }
};


// Track active session location (call on auth state change / login)
let lastTracked = 0;
const TRACK_INTERVAL = 1000 * 60 * 60; // At most once per hour

export const trackSessionLocation = async (userId: string) => {
  const now = Date.now();
  if (now - lastTracked < TRACK_INTERVAL) return;
  lastTracked = now;

  // Count this user as active today (once per day per device, fire-and-forget).
  try {
    const activeKey = `nightspy_active_day_${userId}`;
    const today = new Date().toDateString();
    if (localStorage.getItem(activeKey) !== today) {
      localStorage.setItem(activeKey, today);
      void supabase.rpc("record_daily_activity" as any);
    }
  } catch {
    // ignore storage issues
  }

  const loc = await captureLocation();
  if (!loc.country) return;

  // The IP guess counts as a weak signal, at most once a day.
  try {
    const today = new Date().toDateString();
    if (loc.city && localStorage.getItem("nightspy_ip_signal_day") !== today) {
      recordCitySignal(loc.city, "ip", loc.country);
      localStorage.setItem("nightspy_ip_signal_day", today);
    }
  } catch {
    // ignore storage issues
  }

  // Purchases are the strongest signal; read them from the user's own tickets.
  const purchases: Array<{ city: string; signal: "purchase"; at: number; country: string | null }> = [];
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email) {
      const { data } = await supabase
        .from("tickets")
        .select("created_at, event:events(city, country)")
        .eq("attendee_email", user.email)
        .in("status", ["approved", "checked_in"])
        .order("created_at", { ascending: false })
        .limit(50);
      for (const t of (data as any[]) || []) {
        if (t.event?.city) purchases.push({ city: t.event.city, signal: "purchase", at: new Date(t.created_at).getTime(), country: t.event.country ?? null });
      }
    }
  } catch {
    // purchases are optional
  }

  const inferred = getInferredCity(purchases);

  await supabase.from("profiles").update({
    last_seen_country: loc.country,
    last_seen_city: loc.city,
    last_seen_at: new Date().toISOString(),
    inferred_city: inferred?.city ?? null,
    inferred_country: inferred?.country ?? null,
  } as any).eq("id", userId);
};
